import React, { useState, useEffect, useCallback } from 'react';
import PageLayout from '../components/pagelayout';
import './contentManagement.css';
import { supabase } from '../supabase';

// ─── Tab definitions (matching actual website sections) ───────────────────────
const TABS = [
  { id: 'hero',        label: 'Hero Section' },
  { id: 'honored',     label: 'Honored Personality' },
  { id: 'vendors',     label: 'Top Vendors' },
  { id: 'about',       label: 'About / Legacy' },
  { id: 'history',     label: 'History Timeline' },
  { id: 'events',      label: 'Event Schedule' },
  { id: 'ticketing',   label: 'Ticketing' },
  { id: 'info',        label: 'Fair Info' },
  { id: 'footer',      label: 'Footer' },
  { id: 'popups',      label: 'Popups' },
];

// ─── Default / fallback content (mirrors real website data) ──────────────────
const DEFAULT_CONTENT = {
  hero: {
    edition_tagline: 'Considered one of the largest book fairs in the Middle East',
    edition_number: '58th',
    honored_name: 'Radwa Ashour',
    hero_quote: 'But time does not disclose its secrets to humankind, and the possible turned impossible.',
    hero_quote_author: 'Radwa Ashour',
    cta_primary_label: 'Book Now',
    cta_secondary_label: 'Visitor Guide',
  },
  honored: {
    section_tagline: 'Celebrating Literary Voices',
    section_subtitle: "This year's honored personalities",
    personality_name: 'Radwa Ashour',
    personality_title: 'Egyptian Writer & Scholar',
    personality_quote: 'But time does not disclose its secrets to humankind, and the possible turned impossible.',
    learn_more_label: 'Learn More',
  },
  vendors: {
    section_tagline: 'Our Top Vendors',
    section_subtitle: "Don't forget to pass by them",
    vendor_1_name: 'Diwali',
    vendor_1_hall: 'Hall 2',
    vendor_1_booth: 'C57',
    vendor_2_name: 'Bibliothek',
    vendor_2_hall: 'Hall 2',
    vendor_2_booth: 'C34',
    vendor_3_name: 'عصير الكتب',
    vendor_3_hall: 'Hall 1',
    vendor_3_booth: 'B53',
    explore_more_label: 'Explore More',
  },
  about: {
    section_tagline: 'A Legacy of Literature',
    body_text: 'Since 1969, the Cairo International Book Fair has been the beating heart of Arabic literature and culture. What began as a small gathering has grown into the largest book fair in the Arab world, attracting millions of visitors from across the globe.\n\nEach year, we celebrate the power of words to inspire, educate, and unite people from all walks of life.',
    stat_1_value: '55+',
    stat_1_label: 'Years',
    stat_2_value: '2M+',
    stat_2_label: 'Visitors',
    stat_3_value: '1500+',
    stat_3_label: 'Publishers',
    explore_history_label: 'Explore History',
  },
  history: {
    page_title: 'History',
    milestone_1_year: '1969',
    milestone_1_title: 'The Beginning',
    milestone_1_text: 'The Cairo International Book Fair was founded by the General Egyptian Book Organization to celebrate the 1000th anniversary of Cairo. From its very first edition, the fair aimed to make books accessible to the public while creating a cultural space that brings together readers, writers, and publishers.',
    milestone_2_year: '1984',
    milestone_2_title: 'International Expansion',
    milestone_2_text: 'International participation expanded significantly. The fair moved to larger venues to accommodate the growing number of publishers and visitors from across the Arab world and beyond.',
    milestone_3_year: '2002',
    milestone_3_title: 'Million Milestone',
    milestone_3_text: 'Over one million visitors attended for the first time in the fair\'s history, cementing its status as the largest cultural event in the Arab world.',
    milestone_4_year: '2016',
    milestone_4_title: 'Digital Era',
    milestone_4_text: 'Digital catalogues and online ticketing introduced to modernise the experience for a new generation of book lovers.',
    milestone_5_year: '2024',
    milestone_5_title: 'Record Breaking',
    milestone_5_text: 'Record-breaking attendance of 3.2 million visitors across 18 days, the highest in the fair\'s history.',
  },
  events: {
    section_tagline: 'Event Schedule',
    section_subtitle: "Don't miss these exciting events",
    get_reminders_label: 'Get Reminders',
    event_1_date: '2027-01-28',
    event_1_time: '10:00 AM',
    event_1_type: 'Author Talk',
    event_1_title: 'Memory & Identity in Literature',
    event_2_date: '2027-01-28',
    event_2_time: '12:30 PM',
    event_2_type: 'Signing',
    event_2_title: 'Mirna El Mahdy',
    event_3_date: '2027-01-28',
    event_3_time: '3:00 PM',
    event_3_type: 'Panel Discussion',
    event_3_title: 'Women in Arabic Literature',
  },
  ticketing: {
    page_title: 'Book your ticket',
    fair_dates: 'January 25 – February 7, 2027',
    ticket_type_1_name: 'One Day Pass',
    ticket_type_1_price: 9,
    ticket_type_1_currency: 'EGP',
    ticket_type_2_name: 'VIP',
    ticket_type_2_price: 200,
    ticket_type_2_currency: 'EGP',
    max_tickets_per_booking: 10,
    rules_text: 'Tickets are non-refundable. Children under 12 enter free when accompanied by a ticketed adult. VIP tickets include priority entry and exclusive lounge access.',
  },
  info: {
    page_title: 'Fair Information',
    intro_text: 'The Cairo International Book Fair returns for its 58th edition in 2027, welcoming millions of visitors to one of the largest and most influential cultural events in the Arab world.',
    dates: 'January 23 to February 5, 2027',
    location: 'Egypt International Exhibition Center (EIEC)',
    hours_weekday: 'Daily: 10:00 AM – 8:00 PM',
    hours_weekend: 'Thursdays & Fridays: 10:00 AM – 9:00 PM',
    bus_lines: 'Line 1: Abdel Moneim Riad → Ramses → Abbasia → Al-Azhar University → Book Fair\nLine 2: Shubra → Ahmed Helmy → Ramses → Abbasia → Book Fair\nLine 3: Hegaz Square → Nasr City → Book Fair\nLine 4: El-Matareya → Ain Shams → Book Fair\nLine 5: Giza → Cairo University → Autostrad → Book Fair\nLine 6: Maadi → Ring Road → Book Fair\nLine 7: Heliopolis → Nasr City → Book Fair',
    accessibility_notes: 'Step-free access to all halls\nDedicated accessibility team on-site\nReserved seating areas in main stages\nAssistance available at information points\nClear navigation and signage system\nAccessible digital features (screen reader support, audio guidance)',
    food_notes: 'Food courts across different halls\nSeating areas for rest and socializing',
    safety_notes: 'Bag checks at entry\nMedical team on-site\nClear signage and staff available to assist',
  },
  footer: {
    copyright_text: '© 2026 Cairo International Book Fair. All rights reserved.',
    privacy_policy_url: '/privacy',
    terms_url: '/terms',
    cookies_url: '/cookies',
    social_facebook: 'https://facebook.com/cairobookfair',
    social_instagram: 'https://instagram.com/cairobookfair',
    social_twitter: 'https://twitter.com/cairobookfair',
    social_youtube: 'https://youtube.com/cairobookfair',
  },
  popups: {
    welcome_enabled: true,
    welcome_title: 'Welcome to CIBF 2027!',
    welcome_body: 'Book your tickets early and enjoy exclusive early-bird discounts. Don\'t miss Egypt\'s greatest literary celebration.',
    welcome_cta_label: 'Get Tickets',
    newsletter_enabled: false,
    newsletter_title: 'Stay in the Loop',
    newsletter_body: 'Sign up for event alerts, author announcements, and exclusive offers delivered straight to your inbox.',
    newsletter_cta_label: 'Subscribe Now',
  },
};

// ─── Reusable field components ────────────────────────────────────────────────

function FieldGroup({ label, hint, children }) {
  return (
    <div className="cm-field-group">
      <label className="cm-field-label">
        {label}
        {hint && <span className="cm-field-hint">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder }) {
  return (
    <input
      className="cm-input"
      type="text"
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

function NumberInput({ value, onChange, placeholder, min, max }) {
  return (
    <input
      className="cm-input"
      type="number"
      value={value ?? ''}
      onChange={e => onChange(Number(e.target.value))}
      placeholder={placeholder}
      min={min}
      max={max}
    />
  );
}

function Textarea({ value, onChange, placeholder, rows = 4 }) {
  return (
    <textarea
      className="cm-textarea"
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
    />
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <div className="cm-toggle-row">
      <span className="cm-toggle-label">{label}</span>
      <button
        className={`cm-toggle ${checked ? 'on' : 'off'}`}
        onClick={() => onChange(!checked)}
        aria-pressed={checked}
      >
        <span className="cm-toggle-thumb" />
      </button>
    </div>
  );
}

function SectionCard({ title, icon, children }) {
  return (
    <div className="cm-section-card">
      <div className="cm-section-card-header">
        <span className="cm-section-card-icon">{icon}</span>
        <span className="cm-section-card-title">{title}</span>
      </div>
      <div className="cm-section-card-body">{children}</div>
    </div>
  );
}

function ActionBar({ onSave, saved, saving, error }) {
  return (
    <div className="cm-action-bar">
      {error && <span className="cm-error-badge">⚠ {error}</span>}
      {saved && !error && (
        <span className="cm-saved-badge">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Saved to Supabase
        </span>
      )}
      <button className="cm-btn-primary" onClick={onSave} disabled={saving}>
        {saving ? (
          <>
            <span className="cm-spinner" />
            Saving…
          </>
        ) : (
          <>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v14a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="17,21 17,13 7,13 7,21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Save Changes
          </>
        )}
      </button>
    </div>
  );
}

// ─── Tab Panels ───────────────────────────────────────────────────────────────

const BookIcon = <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M4 6h16M4 12h10M4 18h7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>;
const StarIcon = <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const StoreIcon = <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M3 9l9-6 9 6v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke="currentColor" strokeWidth="1.8"/></svg>;
const ChartIcon = <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M18 20V10M12 20V4M6 20v-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>;
const ClockIcon = <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/><path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>;
const CalIcon = <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>;
const TicketIcon = <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M2 9a3 3 0 010-6h20a3 3 0 010 6v6a3 3 0 010 6H2a3 3 0 010-6V9z" stroke="currentColor" strokeWidth="1.8"/></svg>;
const InfoIcon = <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/><path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>;
const ShareIcon = <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="1.8"/><circle cx="6" cy="12" r="3" stroke="currentColor" strokeWidth="1.8"/><circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="1.8"/><path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>;
const PopupIcon = <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.8"/><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>;

function HeroPanel({ data, onChange }) {
  const f = k => v => onChange({ ...data, [k]: v });
  return (
    <>
      <SectionCard title="Hero Text & Taglines" icon={BookIcon}>
        <FieldGroup label="Edition Tagline" hint="Shown top-left on hero">
          <TextInput value={data.edition_tagline} onChange={f('edition_tagline')} placeholder="Considered one of the largest…" />
        </FieldGroup>
        <div className="cm-two-col">
          <FieldGroup label="Edition Number" hint="e.g. 58th">
            <TextInput value={data.edition_number} onChange={f('edition_number')} placeholder="58th" />
          </FieldGroup>
          <FieldGroup label="Honored Person Name">
            <TextInput value={data.honored_name} onChange={f('honored_name')} placeholder="Radwa Ashour" />
          </FieldGroup>
        </div>
        <FieldGroup label="Hero Quote">
          <Textarea value={data.hero_quote} onChange={f('hero_quote')} rows={3} />
        </FieldGroup>
        <FieldGroup label="Quote Author">
          <TextInput value={data.hero_quote_author} onChange={f('hero_quote_author')} />
        </FieldGroup>
      </SectionCard>
      <SectionCard title="CTA Buttons" icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="3" y="7" width="18" height="10" rx="5" stroke="currentColor" strokeWidth="1.8"/></svg>}>
        <div className="cm-two-col">
          <FieldGroup label="Primary Button">
            <TextInput value={data.cta_primary_label} onChange={f('cta_primary_label')} placeholder="Book Now" />
          </FieldGroup>
          <FieldGroup label="Secondary Button">
            <TextInput value={data.cta_secondary_label} onChange={f('cta_secondary_label')} placeholder="Visitor Guide" />
          </FieldGroup>
        </div>
      </SectionCard>
    </>
  );
}

function HonoredPanel({ data, onChange }) {
  const f = k => v => onChange({ ...data, [k]: v });
  return (
    <SectionCard title="Honored Personality Section" icon={StarIcon}>
      <div className="cm-two-col">
        <FieldGroup label="Section Tagline" hint="In curly braces on site">
          <TextInput value={data.section_tagline} onChange={f('section_tagline')} placeholder="Celebrating Literary Voices" />
        </FieldGroup>
        <FieldGroup label="Section Subtitle">
          <TextInput value={data.section_subtitle} onChange={f('section_subtitle')} placeholder="This year's honored personalities" />
        </FieldGroup>
      </div>
      <div className="cm-two-col">
        <FieldGroup label="Personality Name">
          <TextInput value={data.personality_name} onChange={f('personality_name')} placeholder="Radwa Ashour" />
        </FieldGroup>
        <FieldGroup label="Personality Title">
          <TextInput value={data.personality_title} onChange={f('personality_title')} placeholder="Egyptian Writer & Scholar" />
        </FieldGroup>
      </div>
      <FieldGroup label="Featured Quote">
        <Textarea value={data.personality_quote} onChange={f('personality_quote')} rows={3} />
      </FieldGroup>
      <FieldGroup label="Learn More Button Label">
        <TextInput value={data.learn_more_label} onChange={f('learn_more_label')} placeholder="Learn More" />
      </FieldGroup>
    </SectionCard>
  );
}

function VendorsPanel({ data, onChange }) {
  const f = k => v => onChange({ ...data, [k]: v });
  return (
    <>
      <SectionCard title="Section Labels" icon={StoreIcon}>
        <div className="cm-two-col">
          <FieldGroup label="Section Tagline">
            <TextInput value={data.section_tagline} onChange={f('section_tagline')} placeholder="Our Top Vendors" />
          </FieldGroup>
          <FieldGroup label="Section Subtitle">
            <TextInput value={data.section_subtitle} onChange={f('section_subtitle')} placeholder="Don't forget to pass by them" />
          </FieldGroup>
        </div>
        <FieldGroup label="Explore More Button Label">
          <TextInput value={data.explore_more_label} onChange={f('explore_more_label')} placeholder="Explore More" />
        </FieldGroup>
      </SectionCard>
      <SectionCard title="Featured Vendors (3 slots)" icon={StoreIcon}>
        {[1, 2, 3].map(n => (
          <div key={n} className="cm-milestone-row" style={{ alignItems: 'flex-end' }}>
            <div style={{ flex: 2 }}>
              <FieldGroup label={`Vendor ${n} Name`}>
                <TextInput value={data[`vendor_${n}_name`]} onChange={f(`vendor_${n}_name`)} placeholder="Vendor name" />
              </FieldGroup>
            </div>
            <div style={{ flex: 1 }}>
              <FieldGroup label="Hall">
                <TextInput value={data[`vendor_${n}_hall`]} onChange={f(`vendor_${n}_hall`)} placeholder="Hall 2" />
              </FieldGroup>
            </div>
            <div style={{ flex: 1 }}>
              <FieldGroup label="Booth">
                <TextInput value={data[`vendor_${n}_booth`]} onChange={f(`vendor_${n}_booth`)} placeholder="C57" />
              </FieldGroup>
            </div>
          </div>
        ))}
      </SectionCard>
    </>
  );
}

function AboutPanel({ data, onChange }) {
  const f = k => v => onChange({ ...data, [k]: v });
  return (
    <>
      <SectionCard title="Legacy Section Copy" icon={BookIcon}>
        <FieldGroup label="Section Tagline">
          <TextInput value={data.section_tagline} onChange={f('section_tagline')} placeholder="A Legacy of Literature" />
        </FieldGroup>
        <FieldGroup label="Body Text" hint="Supports line breaks">
          <Textarea value={data.body_text} onChange={f('body_text')} rows={6} />
        </FieldGroup>
        <FieldGroup label="Explore History Button Label">
          <TextInput value={data.explore_history_label} onChange={f('explore_history_label')} placeholder="Explore History" />
        </FieldGroup>
      </SectionCard>
      <SectionCard title="Statistics" icon={ChartIcon}>
        <div className="cm-three-col">
          {[1, 2, 3].map(n => (
            <div key={n} className="cm-stat-edit-block">
              <FieldGroup label={`Stat ${n} Value`}>
                <TextInput value={data[`stat_${n}_value`]} onChange={f(`stat_${n}_value`)} placeholder="55+" />
              </FieldGroup>
              <FieldGroup label="Label">
                <TextInput value={data[`stat_${n}_label`]} onChange={f(`stat_${n}_label`)} placeholder="Years" />
              </FieldGroup>
            </div>
          ))}
        </div>
      </SectionCard>
    </>
  );
}

function HistoryPanel({ data, onChange }) {
  const f = k => v => onChange({ ...data, [k]: v });
  return (
    <>
      <SectionCard title="Page Header" icon={ClockIcon}>
        <FieldGroup label="Page Title">
          <TextInput value={data.page_title} onChange={f('page_title')} placeholder="History" />
        </FieldGroup>
      </SectionCard>
      <SectionCard title="Timeline Milestones" icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}>
        {[1, 2, 3, 4, 5].map(n => (
          <div key={n} className="cm-faq-item">
            <span className="cm-faq-num" style={{ minWidth: 40 }}>{DEFAULT_CONTENT.history[`milestone_${n}_year`] || n}</span>
            <div className="cm-faq-fields">
              <div className="cm-two-col">
                <FieldGroup label="Year">
                  <TextInput value={data[`milestone_${n}_year`]} onChange={f(`milestone_${n}_year`)} placeholder="1969" />
                </FieldGroup>
                <FieldGroup label="Section Title">
                  <TextInput value={data[`milestone_${n}_title`]} onChange={f(`milestone_${n}_title`)} placeholder="The Beginning" />
                </FieldGroup>
              </div>
              <FieldGroup label="Description">
                <Textarea value={data[`milestone_${n}_text`]} onChange={f(`milestone_${n}_text`)} rows={3} />
              </FieldGroup>
            </div>
          </div>
        ))}
      </SectionCard>
    </>
  );
}

function EventsPanel({ data, onChange }) {
  const f = k => v => onChange({ ...data, [k]: v });
  return (
    <>
      <SectionCard title="Section Labels" icon={CalIcon}>
        <div className="cm-two-col">
          <FieldGroup label="Section Tagline">
            <TextInput value={data.section_tagline} onChange={f('section_tagline')} placeholder="Event Schedule" />
          </FieldGroup>
          <FieldGroup label="Section Subtitle">
            <TextInput value={data.section_subtitle} onChange={f('section_subtitle')} placeholder="Don't miss these exciting events" />
          </FieldGroup>
        </div>
        <FieldGroup label="Get Reminders Button Label">
          <TextInput value={data.get_reminders_label} onChange={f('get_reminders_label')} placeholder="Get Reminders" />
        </FieldGroup>
      </SectionCard>
      <SectionCard title="Featured Events (Homepage — 3 slots)" icon={CalIcon}>
        <div className="cm-info-callout">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/><path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
          These 3 events appear on the homepage schedule widget. Full scheduling is managed in the <strong>Scheduling</strong> section.
        </div>
        {[1, 2, 3].map(n => (
          <div key={n} className="cm-faq-item">
            <span className="cm-faq-num">{n}</span>
            <div className="cm-faq-fields">
              <div className="cm-three-col">
                <FieldGroup label="Date">
                  <TextInput value={data[`event_${n}_date`]} onChange={f(`event_${n}_date`)} placeholder="2027-01-28" />
                </FieldGroup>
                <FieldGroup label="Time">
                  <TextInput value={data[`event_${n}_time`]} onChange={f(`event_${n}_time`)} placeholder="10:00 AM" />
                </FieldGroup>
                <FieldGroup label="Type">
                  <TextInput value={data[`event_${n}_type`]} onChange={f(`event_${n}_type`)} placeholder="Author Talk" />
                </FieldGroup>
              </div>
              <FieldGroup label="Event Title">
                <TextInput value={data[`event_${n}_title`]} onChange={f(`event_${n}_title`)} placeholder="Memory & Identity in Literature" />
              </FieldGroup>
            </div>
          </div>
        ))}
      </SectionCard>
    </>
  );
}

function TicketingPanel({ data, onChange }) {
  const f = k => v => onChange({ ...data, [k]: v });
  return (
    <>
      <SectionCard title="Page Header" icon={TicketIcon}>
        <FieldGroup label="Page Title">
          <TextInput value={data.page_title} onChange={f('page_title')} placeholder="Book your ticket" />
        </FieldGroup>
        <FieldGroup label="Fair Dates" hint="Shown below the date picker">
          <TextInput value={data.fair_dates} onChange={f('fair_dates')} placeholder="January 25 – February 7, 2027" />
        </FieldGroup>
        <FieldGroup label="Max Tickets Per Booking">
          <NumberInput value={data.max_tickets_per_booking} onChange={f('max_tickets_per_booking')} min={1} max={50} />
        </FieldGroup>
        <FieldGroup label="Rules & Information Text">
          <Textarea value={data.rules_text} onChange={f('rules_text')} rows={4} />
        </FieldGroup>
      </SectionCard>
      <SectionCard title="Ticket Types" icon={TicketIcon}>
        {[1, 2].map(n => (
          <div key={n} className="cm-milestone-row" style={{ alignItems: 'flex-end' }}>
            <div style={{ flex: 3 }}>
              <FieldGroup label={`Ticket Type ${n} Name`}>
                <TextInput value={data[`ticket_type_${n}_name`]} onChange={f(`ticket_type_${n}_name`)} placeholder="One Day Pass" />
              </FieldGroup>
            </div>
            <div style={{ flex: 1 }}>
              <FieldGroup label="Price">
                <NumberInput value={data[`ticket_type_${n}_price`]} onChange={f(`ticket_type_${n}_price`)} placeholder="9" min={0} />
              </FieldGroup>
            </div>
            <div style={{ flex: 1 }}>
              <FieldGroup label="Currency">
                <TextInput value={data[`ticket_type_${n}_currency`]} onChange={f(`ticket_type_${n}_currency`)} placeholder="EGP" />
              </FieldGroup>
            </div>
          </div>
        ))}
      </SectionCard>
    </>
  );
}

function InfoPanel({ data, onChange }) {
  const f = k => v => onChange({ ...data, [k]: v });
  return (
    <>
      <SectionCard title="Page Header" icon={InfoIcon}>
        <FieldGroup label="Page Title">
          <TextInput value={data.page_title} onChange={f('page_title')} placeholder="Fair Information" />
        </FieldGroup>
        <FieldGroup label="Intro Text">
          <Textarea value={data.intro_text} onChange={f('intro_text')} rows={3} />
        </FieldGroup>
      </SectionCard>
      <SectionCard title="Event Details" icon={CalIcon}>
        <div className="cm-two-col">
          <FieldGroup label="Dates">
            <TextInput value={data.dates} onChange={f('dates')} placeholder="January 23 to February 5, 2027" />
          </FieldGroup>
          <FieldGroup label="Location">
            <TextInput value={data.location} onChange={f('location')} placeholder="Egypt International Exhibition Center (EIEC)" />
          </FieldGroup>
        </div>
        <div className="cm-two-col">
          <FieldGroup label="Weekday Hours">
            <TextInput value={data.hours_weekday} onChange={f('hours_weekday')} placeholder="Daily: 10:00 AM – 8:00 PM" />
          </FieldGroup>
          <FieldGroup label="Thurs & Friday Hours">
            <TextInput value={data.hours_weekend} onChange={f('hours_weekend')} placeholder="Thursdays & Fridays: 10:00 AM – 9:00 PM" />
          </FieldGroup>
        </div>
      </SectionCard>
      <SectionCard title="Transportation" icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="1" y="8" width="22" height="12" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M16 8V6a2 2 0 00-2-2h-4a2 2 0 00-2 2v2M12 12v4M8 20v1M16 20v1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}>
        <FieldGroup label="Bus Lines" hint="One line per route">
          <Textarea value={data.bus_lines} onChange={f('bus_lines')} rows={8} />
        </FieldGroup>
      </SectionCard>
      <SectionCard title="Accessibility, Food & Safety" icon={InfoIcon}>
        <FieldGroup label="Accessibility Notes" hint="One bullet per line">
          <Textarea value={data.accessibility_notes} onChange={f('accessibility_notes')} rows={6} />
        </FieldGroup>
        <FieldGroup label="Food & Drink Notes">
          <Textarea value={data.food_notes} onChange={f('food_notes')} rows={3} />
        </FieldGroup>
        <FieldGroup label="Safety & Security Notes">
          <Textarea value={data.safety_notes} onChange={f('safety_notes')} rows={3} />
        </FieldGroup>
      </SectionCard>
    </>
  );
}

function FooterPanel({ data, onChange }) {
  const f = k => v => onChange({ ...data, [k]: v });
  return (
    <>
      <SectionCard title="Legal & Copyright" icon={BookIcon}>
        <FieldGroup label="Copyright Text">
          <TextInput value={data.copyright_text} onChange={f('copyright_text')} />
        </FieldGroup>
        <div className="cm-three-col">
          <FieldGroup label="Privacy Policy URL">
            <TextInput value={data.privacy_policy_url} onChange={f('privacy_policy_url')} placeholder="/privacy" />
          </FieldGroup>
          <FieldGroup label="Terms of Service URL">
            <TextInput value={data.terms_url} onChange={f('terms_url')} placeholder="/terms" />
          </FieldGroup>
          <FieldGroup label="Cookies URL">
            <TextInput value={data.cookies_url} onChange={f('cookies_url')} placeholder="/cookies" />
          </FieldGroup>
        </div>
      </SectionCard>
      <SectionCard title="Social Media Links" icon={ShareIcon}>
        <div className="cm-two-col">
          {['facebook', 'instagram', 'twitter', 'youtube'].map(platform => (
            <FieldGroup key={platform} label={platform.charAt(0).toUpperCase() + platform.slice(1)}>
              <TextInput
                value={data[`social_${platform}`]}
                onChange={f(`social_${platform}`)}
                placeholder={`https://${platform}.com/…`}
              />
            </FieldGroup>
          ))}
        </div>
      </SectionCard>
    </>
  );
}

function PopupsPanel({ data, onChange }) {
  const f = k => v => onChange({ ...data, [k]: v });
  return (
    <>
      <SectionCard title="Welcome Popup" icon={PopupIcon}>
        <Toggle checked={!!data.welcome_enabled} onChange={f('welcome_enabled')} label="Enable welcome popup" />
        <FieldGroup label="Title">
          <TextInput value={data.welcome_title} onChange={f('welcome_title')} />
        </FieldGroup>
        <FieldGroup label="Body Text">
          <Textarea value={data.welcome_body} onChange={f('welcome_body')} rows={3} />
        </FieldGroup>
        <FieldGroup label="CTA Button Label">
          <TextInput value={data.welcome_cta_label} onChange={f('welcome_cta_label')} />
        </FieldGroup>
      </SectionCard>
      <SectionCard title="Newsletter Popup" icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="1.8"/><polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}>
        <Toggle checked={!!data.newsletter_enabled} onChange={f('newsletter_enabled')} label="Enable newsletter popup" />
        <FieldGroup label="Title">
          <TextInput value={data.newsletter_title} onChange={f('newsletter_title')} />
        </FieldGroup>
        <FieldGroup label="Body Text">
          <Textarea value={data.newsletter_body} onChange={f('newsletter_body')} rows={3} />
        </FieldGroup>
        <FieldGroup label="CTA Button Label">
          <TextInput value={data.newsletter_cta_label} onChange={f('newsletter_cta_label')} />
        </FieldGroup>
      </SectionCard>
    </>
  );
}

// ─── Panel map ────────────────────────────────────────────────────────────────
const PANELS = {
  hero:      HeroPanel,
  honored:   HonoredPanel,
  vendors:   VendorsPanel,
  about:     AboutPanel,
  history:   HistoryPanel,
  events:    EventsPanel,
  ticketing: TicketingPanel,
  info:      InfoPanel,
  footer:    FooterPanel,
  popups:    PopupsPanel,
};

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ContentManagement() {
  const [activeTab, setActiveTab] = useState('hero');
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [search, setSearch] = useState('');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [publishedTab, setPublishedTab] = useState(null);

  // ── Load all sections from Supabase on mount ──
  useEffect(() => {
    async function loadContent() {
      setLoading(true);
      const { data, error } = await supabase
        .from('site_content')
        .select('section, content');

      if (error) {
        console.error('Supabase load error:', error);
        setError('Could not load content from database. Using defaults.');
      } else if (data && data.length > 0) {
        const merged = { ...DEFAULT_CONTENT };
        data.forEach(row => {
          if (merged[row.section] !== undefined) {
            merged[row.section] = { ...merged[row.section], ...row.content };
          }
        });
        setContent(merged);
      }
      setLoading(false);
    }
    loadContent();
  }, []);

  // ── Save current tab's section to Supabase ──
  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);

    const { error } = await supabase
      .from('site_content')
      .upsert(
        { section: activeTab, content: content[activeTab], updated_at: new Date().toISOString() },
        { onConflict: 'section' }
      );

    if (error) {
      console.error('Supabase save error:', error);
      setError('Save failed: ' + error.message);
    } else {
      setSaved(true);
      setPublishedTab(activeTab);
      setTimeout(() => setSaved(false), 3000);
    }
    setSaving(false);
  }, [activeTab, content]);

  const handleChange = useCallback((tabData) => {
    setContent(prev => ({ ...prev, [activeTab]: tabData }));
    setSaved(false);
  }, [activeTab]);

  const filteredTabs = search
    ? TABS.filter(t => t.label.toLowerCase().includes(search.toLowerCase()))
    : TABS;

  const ActivePanel = PANELS[activeTab];

  return (
    <PageLayout activePage="content">
      <div className="cm-page-header">
        <div className="cm-page-header-left">
          <h1 className="cm-page-title">Content Management</h1>
          <p className="cm-page-subtitle">Edit and publish website content — changes are saved to Supabase</p>
        </div>
        {loading && (
          <div className="cm-loading-badge">
            <span className="cm-spinner" />
            Loading…
          </div>
        )}
      </div>

      <div className="cm-toolbar">
        <div className="cm-search-wrap">
          <svg className="cm-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8"/>
            <path d="M20 20l-3-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          <input
            className="cm-search"
            type="text"
            placeholder="Search content sections…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="cm-tabs-row">
        <div className="cm-tabs">
          {filteredTabs.map(tab => (
            <button
              key={tab.id}
              className={`cm-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => { setActiveTab(tab.id); setSaved(false); setError(null); }}
            >
              {tab.label}
              {publishedTab === tab.id && (
                <span className="cm-tab-published-dot" title="Saved" />
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="cm-editor-wrap">
        {loading ? (
          <div className="cm-loading-state">
            <span className="cm-spinner cm-spinner-lg" />
            <p>Loading content from database…</p>
          </div>
        ) : (
          <ActivePanel data={content[activeTab]} onChange={handleChange} />
        )}
        <ActionBar onSave={handleSave} saved={saved} saving={saving} error={error} />
      </div>
    </PageLayout>
  );
}